import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const api = axios.create({ baseURL: BASE });

const TOKEN_KEY = "hrzn_token";

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Centralised 401 handling. If the backend says our token is invalid,
// drop it locally so the auth context can re-bootstrap as a logged-out
// user on the next /api/auth/me call. Without this the UI sometimes
// displayed user A's cached data while the JWT was actually for user B,
// or for nobody at all.
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status === 401 && !url.includes("/api/auth/login") && !url.includes("/api/auth/signup")) {
      try { localStorage.removeItem(TOKEN_KEY); } catch {}
      try { window.dispatchEvent(new CustomEvent("hrzn:auth-expired")); } catch {}
    }
    return Promise.reject(error);
  }
);

export const TIER_LABEL = { built_in: "BUILT-IN", demo: "DEMO", community: "COMMUNITY" };

export const CATEGORIES = [
  { id: "productivity", label: "Productivity" },
  { id: "media", label: "Media" },
  { id: "system", label: "System" },
  { id: "developer", label: "Developer" },
  { id: "utility", label: "Utility" },
  { id: "ai", label: "AI" },
  { id: "integrations", label: "Integrations" },
  { id: "finance", label: "Finance" },
  { id: "creative", label: "Creative" },
];

// Single source of truth for user-facing API errors. Pydantic 422 returns
// `detail` as an array of {loc, msg, type} — passing that to React directly
// throws "Objects are not valid as a React child" and unmounts the page.
export function formatApiError(e, fallback = "Something went wrong. Try again.") {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === "string") return d;
        const loc = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
        const msg = d?.msg || "invalid";
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join(" · ");
  }
  if (detail && typeof detail === "object") {
    try { return JSON.stringify(detail); } catch { return fallback; }
  }
  if (e?.message) return e.message;
  return fallback;
}
